<template>
  <div class="banner-module">
    <div class="main-panel">
      <div class="section-title">活动顶图</div>
      <a-upload
        :action="uploadFileUrl"
        :accept="acceptTypes"
        :show-upload-list="false"
        :before-upload="beforeUploadImage"
        @change="onUploadChange"
      >
        <a-button icon="upload" :loading="uploading">上传文件</a-button>
      </a-upload>
      <div class="upload-tip">支持扩展名：.png .jpg .jpeg</div>

      <div class="ref-image-grid">
        <div
          v-for="(img, index) in refImages"
          :key="img.id || index"
          class="ref-image-item"
          :class="{ selected: isSelectedImage(img) }"
          @mouseenter="hoverRefIndex = index"
          @mouseleave="hoverRefIndex = -1"
        >
          <img class="ref-image" :src="img.url" alt="参考图">
          <div v-if="isSelectedImage(img)" class="selected-tag">已选用</div>
          <div v-show="hoverRefIndex === index" class="image-mask">
            <a-button type="primary" size="small" @click="onUseImage(img)">使用</a-button>
            <a-button
              size="small"
              :loading="img.saving"
              @click="onSaveImage(img, index)"
            >
              保存至素材库
            </a-button>
            <a-button size="small" @click="onRemoveRefImage(index)">移除</a-button>
          </div>
        </div>
        <!-- <div v-if="!refImages.length" class="ref-empty">上传后的图片会显示在这里</div> -->
      </div>

      <div class="bg-color-row">
        <div class="bg-color-label">修改背景颜色值</div>
        <color-palette-picker v-model="value.backgroundColor" />
      </div>
    </div>

    <div class="material-panel">
      <div class="material-header">
        <span class="material-title">蜗友素材库</span>
        <a-input-search
          v-model="materialKeyword"
          placeholder="搜索"
          style="width: 160px"
          allow-clear
          @search="onSearchMaterial"
        />
      </div>
      <a-spin :spinning="materialLoading">
        <div class="material-grid">
          <div
            v-for="item in displayMaterialImages"
            :key="item.id"
            class="material-item"
            @mouseenter="hoverMaterialId = item.id"
            @mouseleave="hoverMaterialId = ''"
          >
            <img v-if="item.url" class="material-image" :src="item.url" :alt="item.name">
            <div v-else class="image-placeholder">{{ item.label || '暂无图片' }}</div>
            <div v-show="item.url && hoverMaterialId === item.id" class="image-mask">
              <a-button type="primary" size="small" @click="onUseMaterial(item)">使用</a-button>
              <a-button
                size="small"
                :loading="item.deleting"
                @click="onDeleteMaterial(item)"
              >
                移除
              </a-button>
            </div>
          </div>
          <div v-if="!displayMaterialImages.length" class="material-empty">
            暂无素材
          </div>
        </div>
      </a-spin>
      <div class="material-pagination">
        <span class="total-text">共 {{ materialTotal }} 张</span>
        <a-pagination
          size="small"
          :current="materialPage"
          :total="materialTotal"
          :page-size="pageSize"
          :show-less-items="true"
          @change="onMaterialPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ColorPalettePicker from '../ColorPalettePicker.vue'

const PAGE_SIZE = 8

function createRefImage (url, name = '') {
  return {
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    url,
    name: name || '参考图',
    saving: false
  }
}

export default {
  name: 'ActivityBannerModule',
  components: {
    ColorPalettePicker
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      uploadFileUrl: this.getUploadFileUrl(),
      acceptTypes: '.png,.jpg,.jpeg',
      uploading: false,
      hoverRefIndex: -1,
      hoverMaterialId: '',
      refImages: [],
      materialKeyword: '',
      materialPage: 1,
      pageSize: PAGE_SIZE,
      materialTotal: 0,
      materialLoading: false,
      materialImages: [],
      /** 新建活动且顶图为空时，素材库加载后默认选用第一张（只自动一次） */
      pendingAutoSelectFirstMaterial: false
    }
  },
  computed: {
    displayMaterialImages () {
      const keyword = (this.materialKeyword || '').trim().toLowerCase()
      if (!keyword) return this.materialImages
      return this.materialImages.filter(item => {
        const name = (item.name || '').toLowerCase()
        const url = (item.url || '').toLowerCase()
        return name.indexOf(keyword) > -1 || url.indexOf(keyword) > -1
      })
    }
  },
  created () {
    // 参考图仅本地临时展示；模块数据只回显已选用的 topImage
    if (this.value && this.value.topImage) {
      this.refImages = [createRefImage(this.value.topImage, '当前顶图')]
    } else {
      // 新建 / 未选顶图：素材库返回后默认用第一张
      this.pendingAutoSelectFirstMaterial = true
    }
    if (this.value && this.value.backgroundColor == null) {
      this.$set(this.value, 'backgroundColor', '')
    }
    this.loadMaterialPage()
  },
  methods: {
    isSelectedImage (img) {
      return !!(img && img.url && this.value && this.value.topImage === img.url)
    },
    getUploadFileUrl () {
      const appConfig = this.getAppConfig()
      return appConfig.baseUrl + (appConfig.isDev ? '/' : '') + 'base/upload'
    },
    getUploadResponseUrl (file) {
      if (!file) return ''
      if (file.url) return file.url
      const response = file.response
      if (!response) return ''
      if (typeof response === 'string') return response
      if (typeof response.data === 'string') return response.data
      if (response.data && typeof response.data.url === 'string') return response.data.url
      if (typeof response.url === 'string') return response.url
      return ''
    },
    beforeUploadImage (file) {
      const fileName = file.name || ''
      const suffix = fileName.substring(fileName.lastIndexOf('.')).toLowerCase()
      const isAccept = ['.png', '.jpg', '.jpeg'].indexOf(suffix) > -1
      if (!isAccept) {
        this.$message.error('上传文件只能是 png、jpg、jpeg 格式')
        return false
      }
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB')
        return false
      }
      return true
    },
    putImageToRef (url, name = '') {
      if (!url) return false
      const exists = this.refImages.some(item => item.url === url)
      if (exists) {
        this.$message.info('该图片已在参考图中')
        return false
      }
      this.refImages.push(createRefImage(url, name || `参考图${this.refImages.length + 1}`))
      return true
    },
    onRemoveRefImage (index) {
      if (index < 0 || index >= this.refImages.length) return
      const removed = this.refImages[index]
      this.refImages.splice(index, 1)
      // 移除的是当前选用图时，同步清空模块 topImage
      if (removed && this.value && this.value.topImage === removed.url) {
        this.$set(this.value, 'topImage', '')
      }
    },
    onUploadChange (info) {
      const { file } = info
      if (file.status === 'uploading') {
        this.uploading = true
        return
      }
      // 不绑受控 file-list，避免 done 丢失一直转圈；有 response.data 即视为成功
      this.uploading = false
      if (file.status === 'error') {
        this.$message.error((file.response && file.response.msg) || '上传失败')
        return
      }
      const url = this.getUploadResponseUrl(file)
      if (url) {
        this.putImageToRef(url, file.name || '上传图片')
        this.$message.success('上传成功')
        return
      }
      if (file.status === 'done' || file.response) {
        this.$message.warning('上传成功但未返回图片地址')
      }
    },
    onUseImage (img, options = {}) {
      if (!img || !img.url) return
      // 模块数据只存这一张选用图，参考图列表不写入 modules
      this.$set(this.value, 'topImage', img.url)
      if (!options.silent) {
        this.$message.success('已选用该图片')
      }
    },
    /** 新建活动：自动选用素材库第一张有地址的图 */
    tryAutoSelectFirstMaterial () {
      if (!this.pendingAutoSelectFirstMaterial) return
      this.pendingAutoSelectFirstMaterial = false
      if (this.value && this.value.topImage) return
      const first = (this.materialImages || []).find(item => item && item.url)
      if (!first) return
      this.onUseImage(first, { silent: true })
      this.putImageToRef(first.url, first.name || '素材')
    },
    /**
     * POST /marketingMaterial/create
     * 入参：materialName、materialType(1图片)、materialUrl、status
     */
    onSaveImage (img, index) {
      if (!img || !img.url || img.saving) return
      this.$set(this.refImages[index], 'saving', true)
      this.getHttpServices()
        .getNewServeService()
        .post('/marketingMaterial/create', {
          materialName: img.name || `素材_${Date.now()}`,
          materialType: 1,
          materialUrl: img.url,
          status: 1
        })
        .then(() => {
          this.$message.success('已保存至素材库')
          this.loadMaterialPage(1)
        })
        .catch(err => {
          this.$message.error((err && err.msg) || '保存素材失败')
        })
        .finally(() => {
          this.$set(this.refImages[index], 'saving', false)
        })
    },
    /**
     * POST /marketingMaterial/page
     * 入参：limitDto、status
     * BaseService resolve 内层 data → { totalCount, data }
     */
    loadMaterialPage (page) {
      if (page) this.materialPage = page
      this.materialLoading = true
      this.getHttpServices()
        .getNewServeService()
        .post('/marketingMaterial/page', {
          status: 1,
          limitDto: {
            pageNum: this.materialPage,
            pageSize: this.pageSize
          }
        })
        .then(res => {
          const list = (res && res.data) || []
          this.materialTotal = (res && res.totalCount) || list.length || 0
          this.materialImages = list.map((item, index) => {
            const id = item.id != null ? item.id : item.materialId
            return {
              id: id != null ? String(id) : `m_${index}_${item.materialUrl || ''}`,
              name: item.materialName || '',
              url: item.materialUrl || '',
              label: item.materialName || '素材',
              deleting: false
            }
          })
          this.tryAutoSelectFirstMaterial()
        })
        .catch(err => {
          this.materialImages = []
          this.materialTotal = 0
          this.pendingAutoSelectFirstMaterial = false
          this.$message.error((err && err.msg) || '获取素材库失败')
        })
        .finally(() => {
          this.materialLoading = false
        })
    },
    onUseMaterial (item) {
      if (!item || !item.url) return
      this.onUseImage(item)
      this.putImageToRef(item.url, item.name || '素材')
    },
    /**
     * POST /marketingMaterial/delete/{id}
     */
    onDeleteMaterial (item) {
      if (!item || item.id == null || item.deleting) return
      const materialId = String(item.id)
      if (!materialId || materialId.indexOf('m_') === 0) {
        this.$message.error('缺少素材ID，无法删除')
        return
      }
      this.$set(item, 'deleting', true)
      this.getHttpServices()
        .getNewServeService()
        .post(`/marketingMaterial/delete/${materialId}`)
        .then(() => {
          this.$message.success('已移除素材')
          // 同步清理参考图 / 已选用顶图
          this.refImages = this.refImages.filter(row => row.url !== item.url)
          if (this.value && this.value.topImage === item.url) {
            this.$set(this.value, 'topImage', '')
          }
          this.loadMaterialPage()
        })
        .catch(err => {
          this.$message.error((err && err.msg) || '移除素材失败')
        })
        .finally(() => {
          this.$set(item, 'deleting', false)
        })
    },
    onSearchMaterial () {
      // 当前分页接口无关键词字段，先对当前页结果做前端过滤
      this.materialPage = 1
      this.loadMaterialPage(1)
    },
    onMaterialPageChange (page) {
      this.loadMaterialPage(page)
    }
  }
}
</script>

<style scoped lang="less">
.banner-module {
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 16px;
}

.main-panel,
.material-panel {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.main-panel {
  flex: 1;
  min-width: 0;
}

.material-panel {
  width: 420px;
  flex-shrink: 0;
}

.section-title {
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.upload-tip {
  margin: 8px 0 16px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
}

.ref-image-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  flex: 1;
  align-content: start;
  max-height: 520px;
  overflow-y: auto;
}

.ref-empty {
  grid-column: 1 / -1;
  padding: 40px 12px;
  text-align: center;
  color: #999;
  font-size: 13px;
}

.ref-image-item,
.material-item {
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 4px;
  background: #f5f5f5;
}

.ref-image-item.selected {
  box-shadow: 0 0 0 2px #fa8c16 inset;
}

.selected-tag {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
  padding: 2px 8px;
  border-radius: 2px;
  background: #fa8c16;
  color: #fff;
  font-size: 12px;
  line-height: 18px;
}

.ref-image,
.material-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  background:
    linear-gradient(to top right, transparent calc(50% - 1px), #d9d9d9, transparent calc(50% + 1px)),
    linear-gradient(to top left, transparent calc(50% - 1px), #d9d9d9, transparent calc(50% + 1px)),
    #f0f0f0;
  background-size: 100% 100%;
}

.image-mask {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.45);
}

.material-empty {
  grid-column: 1 / -1;
  padding: 40px 12px;
  text-align: center;
  color: #999;
  font-size: 13px;
}

.material-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.material-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.material-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  flex: 1;
  align-content: start;
  min-height: 180px;
}

.material-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;

  .total-text {
    color: #999;
    font-size: 12px;
  }
}

.bg-color-row {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.bg-color-label {
  margin-bottom: 8px;
  font-size: 13px;
  color: #333;
}
</style>
